import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendDown,
  faArrowTrendUp,
  faChartLine,
  faCoins,
  faCreditCard,
  faFileInvoiceDollar,
  faHandHoldingDollar,
  faMoneyBillWave,
  faReceipt,
  faRotateRight,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../dash.css';
import { normalizeRequisitionStatus } from './requisitionHelpers';
import {
  formatDateTime,
  formatKes,
  getMonthKey,
  getMonthLabel,
  normalizeAmount,
  resolveBillPaymentPeriodKey,
  resolveRequisitionPaymentPeriodKey,
  resolveSalaryPaymentPeriodKey,
  summarizeFixedBills,
  summarizePayroll,
  summarizeRequisitions,
  toDateValue
} from '../../utils/financeWorkflow';

const buildRecentMonthKeys = (periodKey, count = 6) => {
  const [year, month] = String(periodKey || getMonthKey()).split('-').map(Number);
  const seedDate = new Date(year || new Date().getFullYear(), (month || 1) - 1, 1);

  return Array.from({ length: count }, (_, index) => {
    const nextDate = new Date(seedDate.getFullYear(), seedDate.getMonth() - (count - 1 - index), 1);
    return getMonthKey(nextDate);
  });
};

const groupTotals = (items, keyGetter, amountGetter) => {
  return items.reduce((accumulator, item) => {
    const key = keyGetter(item);
    const amount = normalizeAmount(amountGetter(item));

    if (!key || amount <= 0) {
      return accumulator;
    }

    accumulator[key] = (accumulator[key] || 0) + amount;
    return accumulator;
  }, {});
};

function FinReportsComp() {
  const [loading, setLoading] = useState(true);
  const [periodKey, setPeriodKey] = useState(getMonthKey());
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [income, setIncome] = useState([]);
  const [admissionFees, setAdmissionFees] = useState([]);
  const [otherIncome, setOtherIncome] = useState([]);
  const [bills, setBills] = useState([]);
  const [billPayments, setBillPayments] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [requisitionPayments, setRequisitionPayments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [legacyExpenses, setLegacyExpenses] = useState([]);

  const loadReports = async () => {
    const [
      vendorSnapshot,
      branchSnapshot,
      incomeSnapshot,
      admissionSnapshot,
      otherIncomeSnapshot,
      billSnapshot,
      billPaymentSnapshot,
      requisitionSnapshot,
      requisitionPaymentSnapshot,
      employeeSnapshot,
      salaryPaymentSnapshot,
      expenseSnapshot
    ] = await Promise.all([
      getDocs(collection(db, 'Vendors')),
      getDocs(collection(db, 'Branches')),
      getDocs(collection(db, 'Income')),
      getDocs(collection(db, 'AdmissionFeePayments')),
      getDocs(collection(db, 'otherIncome')),
      getDocs(collection(db, 'Bills')),
      getDocs(collection(db, 'BillPayments')),
      getDocs(collection(db, 'Requisitions')),
      getDocs(collection(db, 'RequisitionPayments')),
      getDocs(collection(db, 'Employees')),
      getDocs(collection(db, 'SalaryPayments')),
      getDocs(collection(db, 'Expenses'))
    ]);

    setVendors(vendorSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setBranches(branchSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setIncome(incomeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setAdmissionFees(admissionSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setOtherIncome(otherIncomeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setBills(billSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setBillPayments(billPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setRequisitions(requisitionSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setRequisitionPayments(requisitionPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setEmployees(employeeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setSalaryPayments(salaryPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setLegacyExpenses(expenseSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
  };

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadReports();
      } catch (error) {
        console.error('Failed to load finance reports:', error);
        swal('Error', 'Finance reports could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, []);

  const vendorsById = useMemo(() => {
    return vendors.reduce((accumulator, vendor) => {
      accumulator[vendor.id] = vendor;
      return accumulator;
    }, {});
  }, [vendors]);

  const branchMap = useMemo(() => {
    return branches.reduce((accumulator, branch) => {
      accumulator[branch.id] = branch.branchName;
      return accumulator;
    }, {});
  }, [branches]);

  const billRows = useMemo(() => {
    return summarizeFixedBills({
      bills,
      vendorsById,
      branchesById: branchMap,
      payments: billPayments,
      periodKey
    });
  }, [billPayments, bills, branchMap, periodKey, vendorsById]);

  const requisitionRows = useMemo(() => {
    return summarizeRequisitions({
      requisitions,
      branchesById: branchMap,
      payments: requisitionPayments
    }).filter((item) => {
      const normalizedStatus = normalizeRequisitionStatus(item.status);
      return normalizedStatus === 'Approved' || normalizedStatus === 'Partially Approved';
    });
  }, [branchMap, requisitionPayments, requisitions]);

  const payrollRows = useMemo(() => {
    return summarizePayroll({
      employees,
      payments: salaryPayments,
      periodKey
    }).filter((employee) => String(employee.employeeStatus || 'Active').trim() !== 'Archived');
  }, [employees, periodKey, salaryPayments]);

  const schoolFeeIncome = useMemo(() => {
    return income
      .filter((item) => getMonthKey(item.paymentDate || item.timeCreated) === periodKey)
      .reduce((sum, item) => sum + normalizeAmount(item.incomeAmount || item.amount), 0);
  }, [income, periodKey]);

  const admissionFeeIncome = useMemo(() => {
    return admissionFees
      .filter((item) => getMonthKey(item.paymentDate || item.createdAt) === periodKey)
      .reduce((sum, item) => sum + normalizeAmount(item.amount || item.admissionFeeAmount), 0);
  }, [admissionFees, periodKey]);

  const otherIncomeValue = useMemo(() => {
    return otherIncome
      .filter((item) => getMonthKey(item.dateReceived || item.timeCreated) === periodKey)
      .reduce((sum, item) => sum + normalizeAmount(item.amount), 0);
  }, [otherIncome, periodKey]);

  const fixedBillSpend = useMemo(() => {
    return billPayments
      .filter((payment) => payment.billId && resolveBillPaymentPeriodKey(payment) === periodKey)
      .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
  }, [billPayments, periodKey]);

  const requisitionSpend = useMemo(() => {
    return requisitionPayments
      .filter((payment) => resolveRequisitionPaymentPeriodKey(payment) === periodKey)
      .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
  }, [periodKey, requisitionPayments]);

  const payrollSpend = useMemo(() => {
    return salaryPayments
      .filter((payment) => resolveSalaryPaymentPeriodKey(payment) === periodKey)
      .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
  }, [periodKey, salaryPayments]);

  const legacySpend = useMemo(() => {
    return legacyExpenses
      .filter((expense) => getMonthKey(expense.expenseDate || expense.timeStamp) === periodKey)
      .reduce((sum, expense) => sum + normalizeAmount(expense.expenseAmount), 0);
  }, [legacyExpenses, periodKey]);

  const totalInflow = schoolFeeIncome + admissionFeeIncome + otherIncomeValue;
  const operationsSpend = fixedBillSpend + requisitionSpend + legacySpend;
  const totalOutflow = operationsSpend + payrollSpend;
  const netCashMovement = totalInflow - totalOutflow;

  const outstandingFixedBills = useMemo(() => {
    return billRows
      .filter((bill) => bill.remainingAmount > 0)
      .sort((first, second) => second.remainingAmount - first.remainingAmount)
      .slice(0, 5);
  }, [billRows]);

  const outstandingRequisitions = useMemo(() => {
    return requisitionRows
      .filter((item) => item.remainingAmount > 0)
      .sort((first, second) => second.remainingAmount - first.remainingAmount)
      .slice(0, 5);
  }, [requisitionRows]);

  const outstandingPayroll = useMemo(() => {
    return payrollRows
      .filter((employee) => employee.remainingAmount > 0)
      .sort((first, second) => second.remainingAmount - first.remainingAmount)
      .slice(0, 5);
  }, [payrollRows]);

  const liabilitySummary = useMemo(() => {
    return {
      fixedBills: billRows.reduce((sum, bill) => sum + normalizeAmount(bill.remainingAmount), 0),
      requisitions: requisitionRows.reduce((sum, item) => sum + normalizeAmount(item.remainingAmount), 0),
      payroll: payrollRows.reduce((sum, employee) => sum + normalizeAmount(employee.remainingAmount), 0)
    };
  }, [billRows, payrollRows, requisitionRows]);

  const paymentMethodMix = useMemo(() => {
    const schoolFeeMethods = groupTotals(
      income.filter((item) => getMonthKey(item.paymentDate || item.timeCreated) === periodKey),
      (item) => item.paymentMethod || 'Not specified',
      (item) => item.incomeAmount || item.amount
    );
    const admissionMethods = groupTotals(
      admissionFees.filter((item) => getMonthKey(item.paymentDate || item.createdAt) === periodKey),
      (item) => item.paymentMethod || item.admissionFeePaymentMethod || 'Not specified',
      (item) => item.amount || item.admissionFeeAmount
    );

    const combined = { ...schoolFeeMethods };
    Object.entries(admissionMethods).forEach(([method, amount]) => {
      combined[method] = (combined[method] || 0) + amount;
    });

    return Object.entries(combined)
      .map(([method, amount]) => ({ method, amount }))
      .sort((first, second) => second.amount - first.amount);
  }, [admissionFees, income, periodKey]);

  const campusObligations = useMemo(() => {
    const fixedBillTotals = billRows.reduce((accumulator, bill) => {
      const campusName = bill.branchName || 'Campus not set';
      const currentItem = accumulator[campusName] || {
        campusName,
        expected: 0,
        paid: 0,
        remaining: 0,
        payroll: 0,
        requisitions: 0
      };

      currentItem.expected += normalizeAmount(bill.expectedAmount);
      currentItem.paid += normalizeAmount(bill.paidAmount);
      currentItem.remaining += normalizeAmount(bill.remainingAmount);
      accumulator[campusName] = currentItem;
      return accumulator;
    }, {});

    requisitionRows.forEach((item) => {
      const campusName = item.branchName || 'Campus not set';
      const currentItem = fixedBillTotals[campusName] || {
        campusName,
        expected: 0,
        paid: 0,
        remaining: 0,
        payroll: 0,
        requisitions: 0
      };

      currentItem.requisitions += normalizeAmount(item.remainingAmount);
      fixedBillTotals[campusName] = currentItem;
    });

    payrollRows.forEach((employee) => {
      const campusName = employee.campus || 'Campus not set';
      const currentItem = fixedBillTotals[campusName] || {
        campusName,
        expected: 0,
        paid: 0,
        remaining: 0,
        payroll: 0,
        requisitions: 0
      };

      currentItem.payroll += normalizeAmount(employee.remainingAmount);
      fixedBillTotals[campusName] = currentItem;
    });

    return Object.values(fixedBillTotals)
      .map((item) => ({
        ...item,
        totalExposure: item.remaining + item.requisitions + item.payroll
      }))
      .sort((first, second) => second.totalExposure - first.totalExposure)
      .slice(0, 6);
  }, [billRows, payrollRows, requisitionRows]);

  const movementFeed = useMemo(() => {
    const inflowItems = [
      ...income.map((item) => ({
        id: `income-${item.id}`,
        direction: 'in',
        title: 'School fee income',
        meta: item.paymentMethod || 'Method not set',
        amount: normalizeAmount(item.incomeAmount || item.amount),
        date: item.paymentDate || item.timeCreated
      })),
      ...admissionFees.map((item) => ({
        id: `adm-${item.id}`,
        direction: 'in',
        title: 'Admission fee',
        meta: item.paymentMethod || item.studentName || 'Admission fee',
        amount: normalizeAmount(item.amount || item.admissionFeeAmount),
        date: item.paymentDate || item.createdAt
      })),
      ...otherIncome.map((item) => ({
        id: `other-${item.id}`,
        direction: 'in',
        title: item.incomeTitle || 'Other income',
        meta: item.incomeFrom || 'Other income',
        amount: normalizeAmount(item.amount),
        date: item.dateReceived || item.timeCreated
      }))
    ];

    const outflowItems = [
      ...billPayments.map((item) => ({
        id: `bill-${item.id}`,
        direction: 'out',
        title: item.billTitle || 'Fixed bill payment',
        meta: item.vendorName || item.payee || 'Vendor',
        amount: normalizeAmount(item.amount),
        date: item.paymentDate || item.createdAt
      })),
      ...requisitionPayments.map((item) => ({
        id: `req-${item.id}`,
        direction: 'out',
        title: item.requisitionTitle || 'Requisition payout',
        meta: item.payee || item.requesterName || 'Staff',
        amount: normalizeAmount(item.amount),
        date: item.paymentDate || item.createdAt
      })),
      ...salaryPayments.map((item) => ({
        id: `salary-${item.id}`,
        direction: 'out',
        title: item.employeeName || 'Salary payment',
        meta: item.salaryPeriodLabel || item.method || 'Payroll',
        amount: normalizeAmount(item.amount),
        date: item.paymentDate || item.createdAt
      })),
      ...legacyExpenses.map((item) => ({
        id: `legacy-${item.id}`,
        direction: 'out',
        title: item.expenseTitle || item.expenseType || 'Legacy expense',
        meta: item.expenseDesc || 'Legacy expense',
        amount: normalizeAmount(item.expenseAmount),
        date: item.expenseDate || item.timeStamp
      }))
    ];

    return [...inflowItems, ...outflowItems]
      .sort((first, second) => {
        const firstTime = toDateValue(first.date)?.getTime() || 0;
        const secondTime = toDateValue(second.date)?.getTime() || 0;
        return secondTime - firstTime;
      })
      .slice(0, 12);
  }, [admissionFees, billPayments, income, legacyExpenses, otherIncome, requisitionPayments, salaryPayments]);

  const trendRows = useMemo(() => {
    const trendMonths = buildRecentMonthKeys(periodKey, 6);
    const maxNetBase = { value: 1 };

    const rows = trendMonths.map((monthKey) => {
      const inflow =
        income
          .filter((item) => getMonthKey(item.paymentDate || item.timeCreated) === monthKey)
          .reduce((sum, item) => sum + normalizeAmount(item.incomeAmount || item.amount), 0) +
        admissionFees
          .filter((item) => getMonthKey(item.paymentDate || item.createdAt) === monthKey)
          .reduce((sum, item) => sum + normalizeAmount(item.amount || item.admissionFeeAmount), 0) +
        otherIncome
          .filter((item) => getMonthKey(item.dateReceived || item.timeCreated) === monthKey)
          .reduce((sum, item) => sum + normalizeAmount(item.amount), 0);

      const outflow =
        billPayments
          .filter((item) => resolveBillPaymentPeriodKey(item) === monthKey)
          .reduce((sum, item) => sum + normalizeAmount(item.amount), 0) +
        requisitionPayments
          .filter((item) => resolveRequisitionPaymentPeriodKey(item) === monthKey)
          .reduce((sum, item) => sum + normalizeAmount(item.amount), 0) +
        salaryPayments
          .filter((item) => resolveSalaryPaymentPeriodKey(item) === monthKey)
          .reduce((sum, item) => sum + normalizeAmount(item.amount), 0) +
        legacyExpenses
          .filter((item) => getMonthKey(item.expenseDate || item.timeStamp) === monthKey)
          .reduce((sum, item) => sum + normalizeAmount(item.expenseAmount), 0);

      const net = inflow - outflow;
      maxNetBase.value = Math.max(maxNetBase.value, Math.abs(net));

      return {
        monthKey,
        label: getMonthLabel(monthKey),
        inflow,
        outflow,
        net
      };
    });

    return rows.map((item) => ({
      ...item,
      netWidth: `${Math.max((Math.abs(item.net) / maxNetBase.value) * 100, item.net === 0 ? 0 : 8)}%`
    }));
  }, [admissionFees, billPayments, income, legacyExpenses, otherIncome, periodKey, requisitionPayments, salaryPayments]);

  const inflowMix = [
    { label: 'School fee income', amount: schoolFeeIncome },
    { label: 'Admission fees', amount: admissionFeeIncome },
    { label: 'Other income', amount: otherIncomeValue }
  ].filter((item) => item.amount > 0 || totalInflow === 0);

  const outflowMix = [
    { label: 'Fixed bills', amount: fixedBillSpend },
    { label: 'Requisition payouts', amount: requisitionSpend },
    { label: 'Payroll', amount: payrollSpend },
    { label: 'Legacy expenses', amount: legacySpend }
  ].filter((item) => item.amount > 0 || totalOutflow === 0);

  const refreshPage = async () => {
    try {
      setLoading(true);
      await loadReports();
    } catch (error) {
      console.error('Failed to refresh finance reports:', error);
      swal('Error', 'Finance reports could not be refreshed right now.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="teamsComponent opsWorkspace opsWorkspace--framed financeReportsPage">
      <section className="opsHero financeReportsHero">
        <div className="opsHeroCopy">
          <span className="opsEyebrow">Finance reports</span>
          <h3>Financial position at a glance</h3>
          <p>
            Follow cash coming in, money going out, and the obligations still waiting on finance for
            the selected month.
          </p>
        </div>
        <div className="opsHeroActions">
          <Form.Control
            type="month"
            value={periodKey}
            onChange={(event) => setPeriodKey(event.target.value)}
            className="financeReportsMonthInput"
          />
          <button type="button" className="opsGhostBtn" onClick={refreshPage}>
            <FontAwesomeIcon icon={faRotateRight} /> Refresh
          </button>
        </div>
      </section>

      {loading ? (
        <div className="opsPanel financeReportsLoading">
          <Spinner animation="border" role="status" />
          <span>Loading finance reports...</span>
        </div>
      ) : (
        <>
          <section className="opsStatsGrid financeReportsStatsGrid">
            <article className="opsStatCard financeReportsStatCard">
              <span>{getMonthLabel(periodKey)} inflow</span>
              <strong>{formatKes(totalInflow)}</strong>
            </article>
            <article className="opsStatCard financeReportsStatCard">
              <span>Operations spend</span>
              <strong>{formatKes(operationsSpend)}</strong>
            </article>
            <article className="opsStatCard financeReportsStatCard">
              <span>Payroll spend</span>
              <strong>{formatKes(payrollSpend)}</strong>
            </article>
            <article className="opsStatCard financeReportsStatCard">
              <span>Net cash movement</span>
              <strong>{formatKes(netCashMovement)}</strong>
            </article>
          </section>

          <section className="financeReportsGrid">
            <article className="opsPanel financeReportsPanel financeReportsPanel--main">
              <div className="opsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faChartLine} />
                </div>
                <div>
                  <h4>Six-month cash trend</h4>
                  <p>Quick read on how inflow and outflow have been moving into the selected month.</p>
                </div>
              </div>
              <div className="financeReportsTrendList">
                {trendRows.map((item) => (
                  <div key={item.monthKey} className="financeReportsTrendRow">
                    <div className="financeReportsTrendMeta">
                      <strong>{item.label}</strong>
                      <small>In {formatKes(item.inflow)} • Out {formatKes(item.outflow)}</small>
                    </div>
                    <div className="financeReportsTrendBarShell">
                      <div
                        className={`financeReportsTrendBar ${item.net >= 0 ? 'positive' : 'negative'}`}
                        style={{ width: item.netWidth }}
                      />
                    </div>
                    <div className={`financeReportsTrendValue ${item.net >= 0 ? 'positive' : 'negative'}`}>
                      {formatKes(item.net)}
                    </div>
                  </div>
                ))}
              </div>
            </article>

            <aside className="opsPanel financeReportsPanel">
              <div className="opsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faCoins} />
                </div>
                <div>
                  <h4>Collections mix</h4>
                  <p>Where money came from during {getMonthLabel(periodKey)}.</p>
                </div>
              </div>
              <div className="financeReportsMixList">
                {inflowMix.map((item) => (
                  <div key={item.label} className="financeReportsMixRow">
                    <div>
                      <strong>{item.label}</strong>
                      <small>
                        {totalInflow > 0
                          ? `${Math.round((item.amount / totalInflow) * 100)}% of monthly inflow`
                          : 'No inflow recorded'}
                      </small>
                    </div>
                    <span>{formatKes(item.amount)}</span>
                  </div>
                ))}
              </div>
            </aside>
          </section>

          <section className="financeReportsGrid">
            <article className="opsPanel financeReportsPanel financeReportsPanel--main">
              <div className="opsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                </div>
                <div>
                  <h4>Spending mix</h4>
                  <p>How the month’s money went out across operations, payroll, and any legacy entries.</p>
                </div>
              </div>
              <div className="financeReportsMixGrid">
                {outflowMix.map((item) => (
                  <div key={item.label} className="financeReportsMixCard">
                    <span>{item.label}</span>
                    <strong>{formatKes(item.amount)}</strong>
                    <small>
                      {totalOutflow > 0
                        ? `${Math.round((item.amount / totalOutflow) * 100)}% of monthly outflow`
                        : 'No outflow recorded'}
                    </small>
                  </div>
                ))}
              </div>
            </article>

            <aside className="opsPanel financeReportsPanel">
              <div className="opsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faCreditCard} />
                </div>
                <div>
                  <h4>Payment methods</h4>
                  <p>Most-used collection methods this month.</p>
                </div>
              </div>
              {paymentMethodMix.length === 0 ? (
                <div className="opsEmptyState opsEmptyState--compact">
                  <h6>No payment methods recorded</h6>
                  <p>Collection methods will appear here once income has been recorded for the month.</p>
                </div>
              ) : (
                <div className="financeReportsMethodList">
                  {paymentMethodMix.map((item) => (
                    <div key={item.method} className="financeReportsMethodRow">
                      <strong>{item.method}</strong>
                      <span>{formatKes(item.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </aside>
          </section>

          <section className="financeReportsGrid">
            <article className="opsPanel financeReportsPanel financeReportsPanel--main">
              <div className="opsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faFileInvoiceDollar} />
                </div>
                <div>
                  <h4>Outstanding commitments</h4>
                  <p>What finance still needs to clear across fixed bills, requisitions, and payroll.</p>
                </div>
              </div>

              <div className="financeReportsLiabilitySummary">
                <div className="financeReportsLiabilityCard">
                  <span>Fixed bill exposure</span>
                  <strong>{formatKes(liabilitySummary.fixedBills)}</strong>
                </div>
                <div className="financeReportsLiabilityCard">
                  <span>Requisition exposure</span>
                  <strong>{formatKes(liabilitySummary.requisitions)}</strong>
                </div>
                <div className="financeReportsLiabilityCard">
                  <span>Payroll exposure</span>
                  <strong>{formatKes(liabilitySummary.payroll)}</strong>
                </div>
              </div>

              <div className="financeReportsLiabilityColumns">
                <div className="financeReportsLiabilityColumn">
                  <h5>Top fixed bills</h5>
                  {outstandingFixedBills.length === 0 ? (
                    <p className="financeReportsMiniEmpty">No fixed bill balances outstanding.</p>
                  ) : (
                    outstandingFixedBills.map((bill) => (
                      <div key={bill.id} className="financeReportsLiabilityRow">
                        <div>
                          <strong>{bill.billTitle}</strong>
                          <small>{bill.vendorName} • {bill.branchName}</small>
                        </div>
                        <span>{formatKes(bill.remainingAmount)}</span>
                      </div>
                    ))
                  )}
                </div>
                <div className="financeReportsLiabilityColumn">
                  <h5>Top requisitions</h5>
                  {outstandingRequisitions.length === 0 ? (
                    <p className="financeReportsMiniEmpty">No approved requisition balances outstanding.</p>
                  ) : (
                    outstandingRequisitions.map((item) => (
                      <div key={item.id} className="financeReportsLiabilityRow">
                        <div>
                          <strong>{item.itemTitle}</strong>
                          <small>{item.requesterName || 'Staff'} • {item.branchName}</small>
                        </div>
                        <span>{formatKes(item.remainingAmount)}</span>
                      </div>
                    ))
                  )}
                </div>
                <div className="financeReportsLiabilityColumn">
                  <h5>Top payroll balances</h5>
                  {outstandingPayroll.length === 0 ? (
                    <p className="financeReportsMiniEmpty">No payroll balances outstanding for this month.</p>
                  ) : (
                    outstandingPayroll.map((employee) => (
                      <div key={employee.id} className="financeReportsLiabilityRow">
                        <div>
                          <strong>{employee.employeeName || 'Employee'}</strong>
                          <small>{employee.jobPosition || 'Role'} • {employee.campus || 'Campus not set'}</small>
                        </div>
                        <span>{formatKes(employee.remainingAmount)}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </article>

            <aside className="opsPanel financeReportsPanel">
              <div className="opsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div>
                  <h4>Campus obligations</h4>
                  <p>Where the biggest remaining finance exposure sits.</p>
                </div>
              </div>
              <div className="financeReportsCampusList">
                {campusObligations.length === 0 ? (
                  <div className="opsEmptyState opsEmptyState--compact">
                    <h6>No campus obligations yet</h6>
                    <p>Campus exposure will appear once bills, requisitions, or payroll are loaded.</p>
                  </div>
                ) : (
                  campusObligations.map((item) => (
                    <div key={item.campusName} className="financeReportsCampusRow">
                      <div>
                        <strong>{item.campusName}</strong>
                        <small>
                          Bills {formatKes(item.remaining)} • Requisitions {formatKes(item.requisitions)} • Payroll {formatKes(item.payroll)}
                        </small>
                      </div>
                      <span>{formatKes(item.totalExposure)}</span>
                    </div>
                  ))
                )}
              </div>
            </aside>
          </section>

          <section className="financeReportsGrid">
            <article className="opsPanel financeReportsPanel financeReportsPanel--main">
              <div className="opsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faReceipt} />
                </div>
                <div>
                  <h4>Recent money movement</h4>
                  <p>Latest inflows and outflows across school fees, operations, requisitions, and payroll.</p>
                </div>
              </div>
              <div className="financeReportsMovementList">
                {movementFeed.map((item) => (
                  <div key={item.id} className="financeReportsMovementRow">
                    <div className={`financeReportsMovementIcon ${item.direction === 'in' ? 'positive' : 'negative'}`}>
                      <FontAwesomeIcon icon={item.direction === 'in' ? faArrowTrendUp : faArrowTrendDown} />
                    </div>
                    <div className="financeReportsMovementBody">
                      <strong>{item.title}</strong>
                      <small>{item.meta}</small>
                    </div>
                    <div className="financeReportsMovementMeta">
                      <strong>{formatKes(item.amount)}</strong>
                      <small>{formatDateTime(item.date)}</small>
                    </div>
                  </div>
                ))}
              </div>
            </article>

            <aside className="opsPanel financeReportsPanel">
              <div className="opsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faHandHoldingDollar} />
                </div>
                <div>
                  <h4>What matters right now</h4>
                  <p>Fast read on the month’s position.</p>
                </div>
              </div>
              <div className="financeReportsSignalStack">
                <div className="financeReportsSignalCard">
                  <strong>{formatKes(totalInflow)}</strong>
                  <span>Total collected in {getMonthLabel(periodKey)}</span>
                </div>
                <div className="financeReportsSignalCard">
                  <strong>{formatKes(totalOutflow)}</strong>
                  <span>Total spent in {getMonthLabel(periodKey)}</span>
                </div>
                <div className={`financeReportsSignalCard ${netCashMovement >= 0 ? 'positive' : 'negative'}`}>
                  <strong>{formatKes(netCashMovement)}</strong>
                  <span>Net movement for the selected month</span>
                </div>
              </div>
            </aside>
          </section>
        </>
      )}
    </div>
  );
}

export default FinReportsComp;
